'use client';

import React from 'react';
import dynamic from 'next/dynamic';
import { EngineSchema } from '@/config/types/engine';

const loading = () => <div className="min-h-32 animate-pulse rounded-2xl bg-slate-100" aria-label="Đang tải tiện ích" />;
const lazy = <T extends React.ComponentType<any>>(loader: () => Promise<{ default: T }>) => dynamic(loader, { ssr: false, loading });
const GrossNetCalculator = lazy(() => import('./GrossNetCalculator').then(m => ({ default: m.GrossNetCalculator })));
const GoldPriceTracker = lazy(() => import('./GoldPriceTracker').then(m => ({ default: m.GoldPriceTracker })));
const SilverPriceTracker = lazy(() => import('./SilverPriceTracker'));
const FuelPriceTracker = lazy(() => import('./FuelPriceTracker'));
const AgriculturePriceTracker = lazy(() => import('./AgriculturePriceTracker'));
const LandPriceTracker = lazy(() => import('./LandPriceTracker'));
const SavingsRateTracker = lazy(() => import('./SavingsRateTracker'));
const LoanRateTracker = lazy(() => import('./LoanRateTracker'));
const LotteryTracker = lazy(() => import('./LotteryTracker'));
const SwiftCodeTracker = lazy(() => import('./SwiftCodeTracker'));
const CurrencyExchangeTracker = lazy(() => import('./CurrencyExchangeTracker').then(m => ({ default: m.CurrencyExchangeTracker })));
const CryptoPriceTracker = lazy(() => import('./CryptoPriceTracker').then(m => ({ default: m.CryptoPriceTracker })));
const LoanCalculator = lazy(() => import('./LoanCalculator').then(m => ({ default: m.LoanCalculator })));
const NumberToWords = lazy(() => import('./NumberToWords').then(m => ({ default: m.NumberToWords })));
const ElectricityCalculator = lazy(() => import('./ElectricityCalculator').then(m => ({ default: m.ElectricityCalculator })));
const CarOnRoadCalculator = lazy(() => import('./CarOnRoadCalculator').then(m => ({ default: m.CarOnRoadCalculator })));
const WordCounter = lazy(() => import('./WordCounter').then(m => ({ default: m.WordCounter })));
const ColorConverter = lazy(() => import('./ColorConverter').then(m => ({ default: m.ColorConverter })));
const JsonFormatter = lazy(() => import('./JsonFormatter').then(m => ({ default: m.JsonFormatter })));
const Base64Converter = lazy(() => import('./Base64Converter').then(m => ({ default: m.Base64Converter })));
const UrlEncoder = lazy(() => import('./UrlEncoder').then(m => ({ default: m.UrlEncoder })));
const PasswordGenerator = lazy(() => import('./PasswordGenerator').then(m => ({ default: m.PasswordGenerator })));
const CodeMinifier = lazy(() => import('./CodeMinifier').then(m => ({ default: m.CodeMinifier })));
const BmiTdeeCalculator = lazy(() => import('./BmiTdeeCalculator').then(m => ({ default: m.BmiTdeeCalculator })));
const ImageCompressor = lazy(() => import('./ImageCompressor').then(m => ({ default: m.ImageCompressor })));
const ImageConverter = lazy(() => import('./ImageConverter').then(m => ({ default: m.ImageConverter })));
const ImageCropper = lazy(() => import('./ImageCropper').then(m => ({ default: m.ImageCropper })));
const VietQRGenerator = lazy(() => import('./VietQRGenerator').then(m => ({ default: m.VietQRGenerator })));
const PdfMerger = lazy(() => import('./PdfMerger').then(m => ({ default: m.PdfMerger })));
const PdfSplitter = lazy(() => import('./PdfSplitter').then(m => ({ default: m.PdfSplitter })));
const FontConverter = lazy(() => import('./FontConverter').then(m => ({ default: m.FontConverter })));
const DocumentOcrExtractor = lazy(() => import('./DocumentOcrExtractor').then(m => ({ default: m.DocumentOcrExtractor })));
const TextDiff = lazy(() => import('./TextDiff').then(m => ({ default: m.TextDiff })));
const FancyTextGenerator = lazy(() => import('./FancyTextGenerator').then(m => ({ default: m.FancyTextGenerator })));
const CleanText = lazy(() => import('./CleanText').then(m => ({ default: m.CleanText })));
const SocialCharCounter = lazy(() => import('./SocialCharCounter').then(m => ({ default: m.SocialCharCounter })));
const LuckyWheel = lazy(() => import('./LuckyWheel').then(m => ({ default: m.LuckyWheel })));
const LunarCalendar = lazy(() => import('./LunarCalendar').then(m => ({ default: m.LunarCalendar })));
const BhxhCalculator = lazy(() => import('./BhxhCalculator').then(m => ({ default: m.BhxhCalculator })));
const AgeCalculator = lazy(() => import('./AgeCalculator').then(m => ({ default: m.AgeCalculator })));
const LandUnitConverter = lazy(() => import('./LandUnitConverter').then(m => ({ default: m.LandUnitConverter })));
const CookingConverter = lazy(() => import('./CookingConverter').then(m => ({ default: m.CookingConverter })));
const LengthDistanceConverter = lazy(() => import('./LengthDistanceConverter').then(m => ({ default: m.LengthDistanceConverter })));
const WeightMassConverter = lazy(() => import('./WeightMassConverter').then(m => ({ default: m.WeightMassConverter })));
const DataStorageConverter = lazy(() => import('./DataStorageConverter').then(m => ({ default: m.DataStorageConverter })));
const SpeedConverter = lazy(() => import('./SpeedConverter').then(m => ({ default: m.SpeedConverter })));
const TemperaturePressureConverter = lazy(() => import('./TemperaturePressureConverter').then(m => ({ default: m.TemperaturePressureConverter })));
const FuelConsumptionConverter = lazy(() => import('./FuelConsumptionConverter').then(m => ({ default: m.FuelConsumptionConverter })));
const PowerEnergyConverter = lazy(() => import('./PowerEnergyConverter').then(m => ({ default: m.PowerEnergyConverter })));
const AngleConverter = lazy(() => import('./AngleConverter').then(m => ({ default: m.AngleConverter })));
const GraduationScoreCalculator = lazy(() => import('./GraduationScoreCalculator').then(m => ({ default: m.GraduationScoreCalculator })));
const GpaCalculator = lazy(() => import('./GpaCalculator').then(m => ({ default: m.GpaCalculator })));
const ScientificCalculator = lazy(() => import('./ScientificCalculator').then(m => ({ default: m.ScientificCalculator })));
const FlashcardMaker = lazy(() => import('./FlashcardMaker').then(m => ({ default: m.FlashcardMaker })));
const PeriodicTable = lazy(() => import('./PeriodicTable').then(m => ({ default: m.PeriodicTable })));
const AverageGradeCalculator = lazy(() => import('./SubjectAverageCalculator').then(m => ({ default: m.AverageGradeCalculator })));
const FunctionGrapher2D = lazy(() => import('./FunctionGrapher2D').then(m => ({ default: m.FunctionGrapher2D })));
const TriangleSolver = lazy(() => import('./TriangleSolver').then(m => ({ default: m.TriangleSolver })));
const PomodoroTimer = lazy(() => import('./PomodoroTimer').then(m => ({ default: m.PomodoroTimer })));
const BaseConverter = lazy(() => import('./BaseConverter').then(m => ({ default: m.BaseConverter })));
const WaterIntakeCalculator = lazy(() => import('./WaterIntakeCalculator').then(m => ({ default: m.WaterIntakeCalculator })));
const OvulationCalculator = lazy(() => import('./OvulationCalculator').then(m => ({ default: m.OvulationCalculator })));
const HiitTimer = lazy(() => import('./HiitTimer').then(m => ({ default: m.HiitTimer })));
const HeartRateZoneCalculator = lazy(() => import('./HeartRateZoneCalculator').then(m => ({ default: m.HeartRateZoneCalculator })));
const BodyFatCalculator = lazy(() => import('./BodyFatCalculator').then(m => ({ default: m.BodyFatCalculator })));
const WhrCalculator = lazy(() => import('./WhrCalculator').then(m => ({ default: m.WhrCalculator })));
const ExerciseCaloriesCalculator = lazy(() => import('./ExerciseCaloriesCalculator').then(m => ({ default: m.ExerciseCaloriesCalculator })));
const BmrCalculator = lazy(() => import('./BmrCalculator').then(m => ({ default: m.BmrCalculator })));
const MacroCalculator = lazy(() => import('./MacroCalculator').then(m => ({ default: m.MacroCalculator })));
const StepToCalorieConverter = lazy(() => import('./StepToCalorieConverter').then(m => ({ default: m.StepToCalorieConverter })));
const StereoSpeakerTester = lazy(() => import('./StereoSpeakerTester').then(m => ({ default: m.StereoSpeakerTester })));
const VoiceRecorder = lazy(() => import('./VoiceRecorder').then(m => ({ default: m.VoiceRecorder })));
const Metronome = lazy(() => import('./Metronome').then(m => ({ default: m.Metronome })));
const ToneGenerator = lazy(() => import('./ToneGenerator').then(m => ({ default: m.ToneGenerator })));
const ScreenRefreshRateTester = lazy(() => import('./ScreenRefreshRateTester').then(m => ({ default: m.ScreenRefreshRateTester })));
const DeadPixelTester = lazy(() => import('./DeadPixelTester').then(m => ({ default: m.DeadPixelTester })));
const KeyboardTester = lazy(() => import('./KeyboardTester').then(m => ({ default: m.KeyboardTester })));
const MouseClickSpeedTester = lazy(() => import('./MouseClickSpeedTester').then(m => ({ default: m.MouseClickSpeedTester })));
const WhiteNoiseGenerator = lazy(() => import('./WhiteNoiseGenerator').then(m => ({ default: m.WhiteNoiseGenerator })));
const OvertimePayCalculator = lazy(() => import('./OvertimePayCalculator').then(m => ({ default: m.OvertimePayCalculator })));
const SeverancePayCalculator = lazy(() => import('./SeverancePayCalculator').then(m => ({ default: m.SeverancePayCalculator })));
const DepreciationCalculator = lazy(() => import('./DepreciationCalculator').then(m => ({ default: m.DepreciationCalculator })));
const SavingsCalculator = lazy(() => import('./SavingsCalculator').then(m => ({ default: m.SavingsCalculator })));
const BreakEvenCalculator = lazy(() => import('./BreakEvenCalculator').then(m => ({ default: m.BreakEvenCalculator })));
const ConstructionCostCalculator = lazy(() => import('./ConstructionCostCalculator').then(m => ({ default: m.ConstructionCostCalculator })));
const FireRetirementPlanner = lazy(() => import('./FireRetirementPlanner').then(m => ({ default: m.FireRetirementPlanner })));
const WatermarkTool = lazy(() => import('./WatermarkTool').then(m => ({ default: m.WatermarkTool })));
const CanvasFilterEditor = lazy(() => import('./CanvasFilterEditor').then(m => ({ default: m.CanvasFilterEditor })));
const AvatarBorderMaker = lazy(() => import('./AvatarBorderMaker').then(m => ({ default: m.AvatarBorderMaker })));
const PaletteExtractor = lazy(() => import('./PaletteExtractor').then(m => ({ default: m.PaletteExtractor })));
const BarcodeGenerator = lazy(() => import('./BarcodeGenerator').then(m => ({ default: m.BarcodeGenerator })));
const PhotoCollageMaker = lazy(() => import('./PhotoCollageMaker').then(m => ({ default: m.PhotoCollageMaker })));
const PdfRotator = lazy(() => import('./PdfRotator').then(m => ({ default: m.PdfRotator })));
const ImageToPdfConverter = lazy(() => import('./ImageToPdfConverter').then(m => ({ default: m.ImageToPdfConverter })));
const PdfPasswordTool = lazy(() => import('./PdfPasswordTool').then(m => ({ default: m.PdfPasswordTool })));
const CaseConverter = lazy(() => import('./CaseConverter').then(m => ({ default: m.CaseConverter })));
const DigitalSignature = lazy(() => import('./DigitalSignature').then(m => ({ default: m.DigitalSignature })));
const RegexDataExtractor = lazy(() => import('./RegexDataExtractor').then(m => ({ default: m.RegexDataExtractor })));
const ZalgoGlitchText = lazy(() => import('./ZalgoGlitchText').then(m => ({ default: m.ZalgoGlitchText })));
const RandomTeamPicker = lazy(() => import('./RandomTeamPicker').then(m => ({ default: m.RandomTeamPicker })));
const HashtagGenerator = lazy(() => import('./HashtagGenerator').then(m => ({ default: m.HashtagGenerator })));
const ListSorter = lazy(() => import('./ListSorter').then(m => ({ default: m.ListSorter })));
const CustomQrCode = lazy(() => import('./CustomQrCode').then(m => ({ default: m.CustomQrCode })));
const BatchFindReplace = lazy(() => import('./BatchFindReplace').then(m => ({ default: m.BatchFindReplace })));
const TextToSpeechPlayer = lazy(() => import('./TextToSpeechPlayer').then(m => ({ default: m.TextToSpeechPlayer })));
const WaterBillCalculator = lazy(() => import('./WaterBillCalculator').then(m => ({ default: m.WaterBillCalculator })));
const UnemploymentBenefitCalculator = lazy(() => import('./UnemploymentBenefitCalculator').then(m => ({ default: m.UnemploymentBenefitCalculator })));
const DueDateCalculator = lazy(() => import('./DueDateCalculator').then(m => ({ default: m.DueDateCalculator })));
const LicensePlateLookup = lazy(() => import('./LicensePlateLookup').then(m => ({ default: m.LicensePlateLookup })));
const ZipCodeLookup = lazy(() => import('./ZipCodeLookup').then(m => ({ default: m.ZipCodeLookup })));
const RoomRentSplitter = lazy(() => import('./RoomRentSplitter').then(m => ({ default: m.RoomRentSplitter })));
const RegexTester = lazy(() => import('./RegexTester').then(m => ({ default: m.RegexTester })));
const JsonToTsConverter = lazy(() => import('./JsonToTsConverter').then(m => ({ default: m.JsonToTsConverter })));
const JsonCsvConverter = lazy(() => import('./JsonCsvConverter').then(m => ({ default: m.JsonCsvConverter })));
const JwtDebugger = lazy(() => import('./JwtDebugger').then(m => ({ default: m.JwtDebugger })));
const HashGenerator = lazy(() => import('./HashGenerator').then(m => ({ default: m.HashGenerator })));
const TimestampConverter = lazy(() => import('./TimestampConverter').then(m => ({ default: m.TimestampConverter })));
const UuidGenerator = lazy(() => import('./UuidGenerator').then(m => ({ default: m.UuidGenerator })));
const CalculatorEngine = lazy(() => import('./engines/CalculatorEngine').then(m => ({ default: m.CalculatorEngine })));
const LookupEngine = lazy(() => import('./engines/LookupEngine').then(m => ({ default: m.LookupEngine })));
const TextEngine = lazy(() => import('./engines/TextEngine').then(m => ({ default: m.TextEngine })));
const MediaEngine = lazy(() => import('./engines/MediaEngine').then(m => ({ default: m.MediaEngine })));

export interface ToolRendererProps {
  componentName: string;
  toolSlug?: string;
  categorySlug?: string;
  toolName?: string;
  engineType?: 'calculator' | 'lookup' | 'text' | 'media' | 'converter';
  schemaPath?: string;
  initialInputs?: Record<string, string | number>;
}

import { ENGINE_SCHEMA_LOADERS } from '@/config/engine-schema-loaders';

function normalizeEngineSchema(moduleValue: any, metadataEngineType?: ToolRendererProps['engineType']): EngineSchema {
  const schema = moduleValue?.default ?? moduleValue?.schema ?? moduleValue;
  const engineType = schema?.engineType ?? metadataEngineType;

  if (!schema || typeof schema !== 'object') {
    throw new Error('Cấu hình công cụ không hợp lệ.');
  }

  if (engineType === 'calculator') {
    if (!Array.isArray(schema.fields) || !Array.isArray(schema.outputs)) {
      throw new Error('Cấu hình công cụ tính toán thiếu trường nhập hoặc kết quả.');
    }

    return {
      ...schema,
      engineType: 'calculator',
      outputs: schema.outputs.map((output: any) => ({
        ...output,
        format: output.format ?? output.type ?? 'number',
      })),
    } as EngineSchema;
  }

  return schema as EngineSchema;
}



function DynamicEngineContainer({
  componentName,
  toolSlug,
  categorySlug,
  toolName,
  engineType,
  initialInputs,
}: ToolRendererProps) {
  const currentKey = `${componentName}-${toolSlug || ''}`;
  const [state, setState] = React.useState<{
    schema: EngineSchema | null;
    isLoading: boolean;
    error: string | null;
    key: string;
  }>({
    schema: null,
    isLoading: true,
    error: null,
    key: currentKey,
  });

  React.useEffect(() => {
    let isMounted = true;

    const loader =
      ENGINE_SCHEMA_LOADERS[componentName] ||
      (toolSlug ? ENGINE_SCHEMA_LOADERS[toolSlug] : undefined);

    const loaderPromise = loader
      ? loader()
      : Promise.reject(new Error(`Không tìm thấy schema cho công cụ "${componentName}"`));

    loaderPromise
      .then((mod) => {
        if (isMounted) {
          setState({
            schema: normalizeEngineSchema(mod, engineType),
            isLoading: false,
            error: null,
            key: currentKey,
          });
        }
      })
      .catch((err) => {
        if (isMounted) {
          setState({
            schema: null,
            isLoading: false,
            error: err?.message || 'Không thể tải cấu hình công cụ.',
            key: currentKey,
          });
        }
      });

    return () => {
      isMounted = false;
    };
  }, [componentName, toolSlug, currentKey, engineType]);

  const isLoading = state.isLoading || state.key !== currentKey;
  const schema = state.schema;
  const error = state.error;

  if (isLoading) {
    return (
      <div data-tool-engine-state="loading" className="p-12 bg-white rounded-2xl border border-slate-200 shadow-xs text-center space-y-3">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-blue-600 border-t-transparent" />
        <p className="text-sm font-semibold text-slate-600">Đang khởi tạo tiện ích trực tiếp trên trình duyệt...</p>
      </div>
    );
  }

  if (error || !schema) {
    return (
      <div data-tool-engine-state="error" className="p-8 bg-rose-50 rounded-2xl border border-rose-200 text-center text-rose-700 space-y-2">
        <p className="font-bold">{error || 'Không tìm thấy cấu hình công cụ.'}</p>
      </div>
    );
  }

  const effectiveType = schema.engineType || engineType;

  switch (effectiveType) {
    case 'calculator':
      return (
        <div data-tool-engine-state="ready">
          <CalculatorEngine
            schema={schema as any}
            toolSlug={toolSlug}
            categorySlug={categorySlug}
            toolName={toolName}
            initialInputs={initialInputs}
          />
        </div>
      );
    case 'lookup':
      return (
        <div data-tool-engine-state="ready">
          <LookupEngine
            schema={schema as any}
            toolSlug={toolSlug}
            categorySlug={categorySlug}
            toolName={toolName}
          />
        </div>
      );
    case 'text':
      return (
        <div data-tool-engine-state="ready">
          <TextEngine
            schema={schema as any}
            toolSlug={toolSlug}
            categorySlug={categorySlug}
            toolName={toolName}
          />
        </div>
      );
    case 'media':
      return (
        <div data-tool-engine-state="ready">
          <MediaEngine
            schema={schema as any}
            toolSlug={toolSlug}
            categorySlug={categorySlug}
            toolName={toolName}
          />
        </div>
      );
    default:
      return (
        <div className="p-8 bg-slate-50 border border-slate-200 rounded-2xl text-center text-slate-600">
          <p className="font-semibold">Engine type &quot;{effectiveType}&quot; chưa được hỗ trợ.</p>
        </div>
      );
  }
}

export function ToolRenderer(props: ToolRendererProps) {
  const { componentName, toolSlug } = props;

  // 1. Check if it is a Factory Engine Tool
  if (
    ENGINE_SCHEMA_LOADERS[componentName] ||
    (toolSlug && ENGINE_SCHEMA_LOADERS[toolSlug]) ||
    props.engineType
  ) {
    return <DynamicEngineContainer {...props} />;
  }

  // 2. Dispatch to 106 Custom Tools
  switch (componentName) {
    case 'GrossNetCalculator':
    case 'TaxCalculator':
      return <GrossNetCalculator />;
    case 'LoanCalculator':
      return <LoanCalculator />;
    case 'NumberToWords':
    case 'NumberToWordsConverter':
      return <NumberToWords />;
    case 'ElectricityCalculator':
    case 'EvnCalculator':
      return <ElectricityCalculator />;
    case 'CarOnRoadCalculator':
    case 'CarRollingCostCalculator':
      return <CarOnRoadCalculator />;
    case 'WordCounter':
      return <WordCounter />;
    case 'ColorConverter':
      return <ColorConverter />;
    case 'JsonFormatter':
      return <JsonFormatter />;
    case 'Base64Converter':
    case 'Base64Tool':
      return <Base64Converter />;
    case 'UrlEncoder':
    case 'UrlEncoderTool':
      return <UrlEncoder />;
    case 'PasswordGenerator':
      return <PasswordGenerator />;
    case 'CodeMinifier':
      return <CodeMinifier />;
    case 'BmiTdeeCalculator':
    case 'BmiCalculator':
      return <BmiTdeeCalculator />;
    case 'ImageCompressor':
      return <ImageCompressor />;
    case 'ImageConverter':
      return <ImageConverter />;
    case 'ImageCropper':
      return <ImageCropper />;
    case 'VietQRGenerator':
    case 'VietQrGenerator':
      return <VietQRGenerator />;
    case 'PdfMerger':
    case 'PdfMerge':
      return <PdfMerger />;
    case 'PdfSplitter':
    case 'PdfSplit':
      return <PdfSplitter />;
    case 'FontConverter':
    case 'FontConvert':
      return <FontConverter />;
    case 'TextDiff':
    case 'DiffChecker':
      return <TextDiff />;
    case 'FancyTextGenerator':
      return <FancyTextGenerator />;
    case 'CleanText':
      return <CleanText />;
    case 'SocialCharCounter':
      return <SocialCharCounter />;
    case 'LuckyWheel':
      return <LuckyWheel />;
    case 'LunarCalendar':
      return <LunarCalendar />;
    case 'BhxhCalculator':
      return <BhxhCalculator />;
    case 'AgeCalculator':
      return <AgeCalculator />;
    // --- Category: Chuyển Đổi Đơn Vị & Đo Lường ---
    case 'LandUnitConverter':
    case 'LandAreaConverter':
      return <LandUnitConverter />;
    case 'CookingConverter':
    case 'CookingUnitConverter':
      return <CookingConverter />;
    case 'LengthDistanceConverter':
    case 'LengthAreaConverter':
      return <LengthDistanceConverter />;
    case 'WeightMassConverter':
      return <WeightMassConverter />;
    case 'DataStorageConverter':
      return <DataStorageConverter />;
    case 'SpeedConverter':
      return <SpeedConverter />;
    case 'TemperaturePressureConverter':
    case 'TempPressureConverter':
      return <TemperaturePressureConverter />;
    case 'FuelConsumptionConverter':
      return <FuelConsumptionConverter />;
    case 'PowerEnergyConverter':
      return <PowerEnergyConverter />;
    case 'AngleConverter':
      return <AngleConverter />;
    // --- Category: Học Tập & Giáo Dục ---
    case 'GraduationScoreCalculator':
    case 'GraduationScore':
      return <GraduationScoreCalculator />;
    case 'GpaCalculator':
    case 'GpaCalc':
      return <GpaCalculator />;
    case 'ScientificCalculator':
    case 'CasioCalculator':
      return <ScientificCalculator />;
    case 'FlashcardMaker':
    case 'Flashcard':
      return <FlashcardMaker />;
    case 'PeriodicTable':
    case 'PeriodicTableMendeleev':
      return <PeriodicTable />;
    case 'AverageGradeCalculator':
    case 'SubjectAverageCalculator':
      return <AverageGradeCalculator />;
    case 'GraphingCalculator2d':
    case 'FunctionGrapher2D':
      return <FunctionGrapher2D />;
    case 'TriangleSolver':
      return <TriangleSolver />;
    case 'PomodoroTimer':
    case 'Pomodoro':
      return <PomodoroTimer />;
    case 'NumberBaseConverter':
    case 'BaseConverter':
      return <BaseConverter />;
    // --- Category: Sức Khỏe & Thể Thao ---
    case 'WaterIntakeCalculator':
      return <WaterIntakeCalculator />;
    case 'OvulationCalculator':
      return <OvulationCalculator />;
    case 'HiitIntervalTimer':
    case 'HiitTimer':
      return <HiitTimer />;
    case 'HeartRateZoneCalculator':
    case 'HeartRateZones':
      return <HeartRateZoneCalculator />;
    case 'BodyFatCalculator':
    case 'UsNavyBodyFat':
      return <BodyFatCalculator />;
    case 'WaistToHipCalculator':
    case 'WhrCalculator':
      return <WhrCalculator />;
    case 'CalorieBurnCalculator':
    case 'ExerciseCaloriesCalculator':
      return <ExerciseCaloriesCalculator />;
    case 'BmrCalculator':
    case 'TdeeCalculator':
    case 'BmrTdeeCalculator':
      return <BmrCalculator />;
    case 'MacroNutrientCalculator':
    case 'MacroCalculator':
    case 'MacronutrientCalculator':
      return <MacroCalculator />;
    case 'StepsToCalorieConverter':
    case 'StepToCalorieConverter':
    case 'StepsToCalories':
      return <StepToCalorieConverter />;
    // --- Category: Âm Thanh & Tiện Ích Thiết Bị ---
    case 'AudioSpeakerTest':
    case 'StereoSpeakerTester':
      return <StereoSpeakerTester />;
    case 'VoiceRecorder':
      return <VoiceRecorder />;
    case 'MetronomeTimer':
    case 'Metronome':
      return <Metronome />;
    case 'ToneFrequencyGenerator':
    case 'ToneGenerator':
      return <ToneGenerator />;
    case 'ScreenRefreshRateTest':
    case 'ScreenRefreshRateTester':
      return <ScreenRefreshRateTester />;
    case 'DeadPixelTest':
    case 'DeadPixelTester':
      return <DeadPixelTester />;
    case 'KeyboardGhostingTest':
    case 'KeyboardTester':
      return <KeyboardTester />;
    case 'MouseCpsTest':
    case 'MouseClickSpeedTester':
      return <MouseClickSpeedTester />;
    case 'WhiteNoiseGenerator':
      return <WhiteNoiseGenerator />;

    // --- Category 1: Kế toán & Tài chính ---
    case 'CryptoPriceTracker':
    case 'CryptoPrice':
    case 'BitcoinPrice':
    case 'bang-gia-crypto-bitcoin-hom-nay':
      return <CryptoPriceTracker />;
    case 'GoldPriceTracker':
    case 'GoldPrice':
    case 'GiaVangSjc':
    case 'gia-vang-sjc-9999-hom-nay':
      return <GoldPriceTracker />;
    case 'SilverPriceTracker':
    case 'SilverPrice':
    case 'gia-bac-hom-nay':
    case 'bang-gia-bac-hom-nay':
      return <SilverPriceTracker />;
    case 'FuelPriceTracker':
    case 'FuelPrice':
    case 'gia-xang-dau-hom-nay':
    case 'tra-cuu-gia-xang-dau':
    case 'bang-gia-xang-dau-petrolimex':
      return <FuelPriceTracker />;
    case 'AgriculturePriceTracker':
    case 'AgriculturePrice':
    case 'gia-nong-san-hom-nay':
    case 'bang-gia-nong-san':
    case 'gia-ca-phe-tieu-heo-hoi':
      return <AgriculturePriceTracker />;
    case 'LandPriceTracker':
    case 'LandPrice':
    case 'tra-cuu-bang-gia-dat':
    case 'tra-cuu-khung-gia-dat-63-tinh-thanh':
    case 'bang-gia-dat-nha-nuoc':
      return <LandPriceTracker />;
    case 'SavingsRateTracker':
    case 'so-sanh-lai-suat-tiet-kiem':
    case 'bang-lai-suat-tiet-kiem-cac-ngan-hang':
      return <SavingsRateTracker />;
    case 'LoanRateTracker':
    case 'bang-lai-suat-vay-mua-nha':
    case 'so-sanh-lai-suat-vay-ngan-hang':
      return <LoanRateTracker />;
    case 'LotteryTracker':
    case 'ket-qua-xo-so-3-mien':
    case 'ket-qua-xo-so-vietlott-hom-nay':
    case 'tra-cuu-ket-qua-xo-so-kien-thiet-3-mien':
      return <LotteryTracker />;
    case 'CurrencyExchangeTracker':
    case 'CurrencyConverter':
    case 'CurrencyExchange':
    case 'ChuyenDoiDonViTienTeNgoaiTe':
    case 'chuyen-doi-don-vi-tien-te-ngoai-te':
    case 'quy-doi-ty-gia-tien-te':
    case 'bang-ty-gia-ngoai-te':
      return <CurrencyExchangeTracker />;
    case 'SwiftCodeTracker':
    case 'BankSwiftCode':
    case 'tra-cuu-ma-swift-code-ngan-hang':
    case 'swift-code-ngan-hang':
    case 'bic-code-ngan-hang':
      return <SwiftCodeTracker />;
    case 'OvertimePayCalculator':
    case 'OvertimeSalaryCalculator':
    case 'OvertimePay':
      return <OvertimePayCalculator />;
    case 'SeverancePayCalculator':
    case 'SeverancePay':
      return <SeverancePayCalculator />;
    case 'DepreciationCalculator':
    case 'FixedAssetDepreciationCalculator':
    case 'Depreciation':
      return <DepreciationCalculator />;
    case 'SavingsCalculator':
    case 'SavingsInterestCalculator':
    case 'SavingsInterest':
      return <SavingsCalculator />;
    case 'BreakEvenCalculator':
    case 'BreakEven':
      return <BreakEvenCalculator />;
    case 'ConstructionCostCalculator':
    case 'HouseConstructionEstimator':
    case 'ConstructionCost':
      return <ConstructionCostCalculator />;
    case 'FireRetirementPlanner':
    case 'FireRetirementCalculator':
    case 'FirePlanner':
      return <FireRetirementPlanner />;

    // --- Category 2: Đồ họa & Media ---
    case 'WatermarkTool':
    case 'Watermark':
      return <WatermarkTool />;
    case 'CanvasFilterEditor':
    case 'PhotoFilter':
      return <CanvasFilterEditor />;
    case 'AvatarBorderMaker':
    case 'AvatarBorder':
      return <AvatarBorderMaker />;
    case 'PaletteExtractor':
    case 'ColorPalette':
      return <PaletteExtractor />;
    case 'BarcodeGenerator':
    case 'Barcode':
      return <BarcodeGenerator />;
    case 'PhotoCollageMaker':
    case 'PhotoCollage':
      return <PhotoCollageMaker />;

    // --- Category 3: Văn phòng & Tài liệu ---
    case 'PdfRotator':
    case 'RotatePdf':
      return <PdfRotator />;
    case 'ImageToPdfConverter':
    case 'ImageToPdf':
      return <ImageToPdfConverter />;
    case 'PdfPasswordTool':
    case 'PdfPassword':
      return <PdfPasswordTool />;
    case 'CaseConverter':
    case 'TextCase':
      return <CaseConverter />;
    case 'DigitalSignature':
    case 'Signature':
      return <DigitalSignature />;
    case 'RegexDataExtractor':
    case 'DataExtractor':
      return <RegexDataExtractor />;
    case 'DocumentOcrExtractor':
    case 'OcrExtractor':
    case 'ImageOcr':
      return <DocumentOcrExtractor />;

    // --- Category 4: Mạng xã hội & Text ---
    case 'ZalgoGlitchText':
    case 'ZalgoGlitch':
      return <ZalgoGlitchText />;
    case 'RandomTeamPicker':
    case 'TeamPicker':
      return <RandomTeamPicker />;
    case 'HashtagGenerator':
    case 'Hashtags':
      return <HashtagGenerator />;
    case 'ListSorter':
    case 'SortList':
      return <ListSorter />;
    case 'CustomQrCode':
    case 'QrCodeGenerator':
      return <CustomQrCode />;
    case 'BatchFindReplace':
    case 'FindReplace':
      return <BatchFindReplace />;
    case 'TextToSpeechPlayer':
    case 'TextToSpeech':
      return <TextToSpeechPlayer />;

    // --- Category 5: Đời sống & Hành chính ---
    case 'WaterBillCalculator':
    case 'WaterBill':
      return <WaterBillCalculator />;
    case 'UnemploymentBenefitCalculator':
    case 'UnemploymentBenefit':
    case 'UnemploymentInsurance':
      return <UnemploymentBenefitCalculator />;
    case 'DueDateCalculator':
    case 'PregnancyDueDate':
      return <DueDateCalculator />;
    case 'LicensePlateLookup':
    case 'LicensePlate':
      return <LicensePlateLookup />;
    case 'ZipCodeLookup':
    case 'ZipCode':
    case 'PostalCode':
      return <ZipCodeLookup />;
    case 'RoomRentSplitter':
    case 'RoomRent':
    case 'RentSplitter':
      return <RoomRentSplitter />;

    // --- Category 6: Tiện ích Lập trình ---
    case 'RegexTester':
    case 'Regex':
      return <RegexTester />;
    case 'JsonToTsConverter':
    case 'JsonToTs':
    case 'JsonToTypescript':
      return <JsonToTsConverter />;
    case 'JsonCsvConverter':
    case 'JsonToCsv':
    case 'CsvToJson':
      return <JsonCsvConverter />;
    case 'JwtDebugger':
    case 'JwtDecoder':
    case 'Jwt':
      return <JwtDebugger />;
    case 'HashGenerator':
    case 'Sha256Generator':
    case 'Hash':
      return <HashGenerator />;
    case 'TimestampConverter':
    case 'UnixTimestamp':
    case 'Timestamp':
      return <TimestampConverter />;
    case 'UuidGenerator':
    case 'GuidGenerator':
    case 'Uuid':
      return <UuidGenerator />;
    default:
      return (
        <div className="p-8 bg-slate-50 border border-slate-200 rounded-2xl text-center text-slate-600">
          <p className="font-semibold text-lg">Đang tải công cụ tiện ích...</p>
        </div>
      );
  }
}
